/**
 * Pure helpers voor de Voertuigen / Materieel module (F3).
 *
 * Bevat:
 *   - kenteken-normalisatie + display-formatter (NL sidecodes 1-13)
 *   - dagen-tot-verloop voor APK / verzekering / contract
 *   - badge-classificatie voor verloopdata (rood / amber / groen)
 *
 * Geen dependencies op Prisma of fetch — bruikbaar in zowel API als UI.
 */

import { VIN_REGEX } from '@streep/types'

// ─── Onzichtbare tekens ──────────────────────────────────────────────────────

/**
 * batch-68 werkplaatsfacturen (FO-341 §5) - tekens die in een PDF-tekstlaag
 * onzichtbaar meekomen: het zachte afbreekstreepje (U+00AD, "S<U+00AD>807<U+00AD>RS"),
 * zero-width spaties/joiners (U+200B-U+200D), richtingstekens (U+200E/U+200F),
 * word joiner (U+2060) en de BOM (U+FEFF).
 */
const ONZICHTBARE_TEKENS = /[\u00AD\u200B-\u200F\u2060\uFEFF]/g

/**
 * Verwijder onzichtbare tekens (zie `ONZICHTBARE_TEKENS`). Pure tekstopschoning
 * voor kenteken, chassisnummer en nummers uit een factuur-extractie; de
 * zichtbare tekst blijft gelijk. Idempotent.
 */
export function verwijderOnzichtbareTekens(input: string): string {
  return input.replace(ONZICHTBARE_TEKENS, '')
}

// ─── Kenteken ───────────────────────────────────────────────────────────────

/**
 * Strip alle niet-alfanumeriek + uppercase. Idempotent. (Een zacht afbreek-
 * streepje is niet-alfanumeriek en verdwijnt dus ook: "S<U+00AD>807<U+00AD>RS" -> "S807RS".)
 */
export function normalizeKenteken(input: string | null | undefined): string {
  if (!input) return ''
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

// ─── Chassisnummer (VIN) ─────────────────────────────────────────────────────

/**
 * Normaliseer een (AI-)VIN vóór schema-validatie: uppercase, zonder spaties,
 * zonder onzichtbare tekens en zonder streepjes. Retourneert null wanneer het
 * resultaat geen geldig VIN is (17 alfanumerieke tekens, `VIN_REGEX`).
 *
 * batch-68 werkplaatsfacturen (FO-341): verplaatst uit
 * `apps/api/src/lib/onderhoudsrapport-mail/parser.ts` (FO-327), zodat de
 * telematica-parser en de werkplaatsfactuur-extractie dezelfde normalisatie
 * delen. Uitgebreid met onzichtbare tekens en streepjes (PDF-tekstlaag).
 */
export function normaliseerVin(input: string | null | undefined): string | null {
  if (!input) return null
  const vin = verwijderOnzichtbareTekens(input)
    .toUpperCase()
    .replace(/[\s\-‐-―]+/g, '')
  return VIN_REGEX.test(vin) ? vin : null
}

/**
 * Format een NL kenteken naar display-vorm met streepjes per sidecode.
 *
 * RDW NL kentekens zijn altijd 6 alfanumerieke chars zonder streepjes.
 * Default-split is 2-2-2 (sidecodes 1-6, alle 2-2-2-vormen); sidecodes met
 * een 3-letter- of 3-cijferblok (7-14) krijgen een aangepaste split via de
 * lettertje/cijfertje-patroontabel. Niet-6-char input wordt na normalize
 * onveranderd teruggegeven.
 *
 * Voorbeelden:
 *   "39PFNJ"   -> "39-PF-NJ"   (sidecode 6:  99-XX-XX)
 *   "1KMR03"   -> "1-KMR-03"   (sidecode 8:  9-XXX-99)
 *   "VD123A"   -> "VD-123-A"   (sidecode 9:  XX-999-X)
 *   "V65PXH"   -> "V-65-PXH"   (sidecode 12: X-99-XXX, de V-reeks bedrijfsauto's)
 *   "123ABC"   -> "12-3A-BC"   (geen sidecode-patroon: 2-2-2 fallback)
 */
export function formatKenteken(input: string | null | undefined): string {
  const k = normalizeKenteken(input)
  if (k.length !== 6) return k

  const isLetter = (c: string) => /[A-Z]/.test(c)
  const isDigit = (c: string) => /[0-9]/.test(c)
  const pat = k
    .split('')
    .map((c) => (isLetter(c) ? 'L' : isDigit(c) ? 'N' : '?'))
    .join('')

  // [a, b] = streepje-posities → `${k[0..a]}-${k[a..b]}-${k[b..6]}`.
  // Alle niet-2-2-2 sidecodes (officiële RDW-nummering 7 t/m 14).
  const customSplits: Record<string, [number, number]> = {
    NNLLLN: [2, 5], // sidecode 7:  99-XXX-9
    NLLLNN: [1, 4], // sidecode 8:  9-XXX-99
    LLNNNL: [2, 5], // sidecode 9:  XX-999-X
    LNNNLL: [1, 4], // sidecode 10: X-999-XX
    LLLNNL: [3, 5], // sidecode 11: XXX-99-X
    LNNLLL: [1, 3], // sidecode 12: X-99-XXX (V-reeks bedrijfsauto's)
    NLLNNN: [1, 3], // sidecode 13: 9-XX-999
    NNNLLN: [3, 5], // sidecode 14: 999-XX-9
  }
  const s = customSplits[pat]
  if (s) {
    return `${k.slice(0, s[0])}-${k.slice(s[0], s[1])}-${k.slice(s[1])}`
  }
  return `${k.slice(0, 2)}-${k.slice(2, 4)}-${k.slice(4, 6)}`
}

// ─── Verloopdata ────────────────────────────────────────────────────────────

/**
 * Aantal hele dagen tussen `from` en `to`, naar boven afgerond als de tijd
 * verschilt. Negatief als `to` in het verleden ligt.
 *
 * Werkt UTC-onafhankelijk: we vergelijken alleen kalenderdagen (ymd-based)
 * zodat tijdzone-verschillen rond middernacht geen off-by-one geven.
 */
export function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  const b = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate())
  return Math.round((b - a) / 86_400_000)
}

/** `null` als datum ontbreekt; anders dagen-tot-verloop t.o.v. `today` (UTC-midnight). */
export function dagenTotVerloop(
  vervaldatum: Date | string | null | undefined,
  today: Date = new Date(),
): number | null {
  if (vervaldatum == null) return null
  const d = vervaldatum instanceof Date ? vervaldatum : new Date(vervaldatum)
  if (Number.isNaN(d.getTime())) return null
  return daysBetween(today, d)
}

export type VerloopBadge = 'verlopen' | 'kritiek' | 'aandacht' | 'ok'

/**
 * Classificeer een verloopdatum naar UI-badge:
 *   - verlopen  : <0 dagen (rood)
 *   - kritiek   : 0..14 dagen (rood/amber)
 *   - aandacht  : 15..30 dagen (amber)
 *   - ok        : >30 dagen (groen)
 */
export function verloopBadge(dagen: number | null): VerloopBadge | null {
  if (dagen == null) return null
  if (dagen < 0) return 'verlopen'
  if (dagen <= 14) return 'kritiek'
  if (dagen <= 30) return 'aandacht'
  return 'ok'
}

export const VERLOOP_BADGE_LABELS: Record<VerloopBadge, string> = {
  verlopen: 'Verlopen',
  kritiek:  'Verloopt binnenkort',
  aandacht: 'Let op',
  ok:       'In orde',
}
